import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChildren,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    NgModule, OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    Renderer2, SimpleChanges,
    ViewChildren,
    ViewContainerRef,
    ViewEncapsulation
} from '@angular/core';
import {AzoraSlot} from "@azora/core";
import {Subject} from "rxjs";
import {coerceBooleanProperty} from "@angular/cdk/coercion";
import {DomSanitizer, SafeStyle} from "@angular/platform-browser";
import _ from 'lodash'

@Component({
    selector: 'az-checkbox',
    templateUrl: './checkbox.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AzoraCheckbox implements OnInit, OnChanges, OnDestroy {
    
    
    @Input() label = ''
    @Input() value = '';
    @Input() name = ''
    
    portals = {
        before: null,
        after: null
    };
    
    private get _inputElement(): HTMLInputElement {
        return this._element.nativeElement.querySelector('input');
    }
    
    
    /** Whether the label should appear after or before the radio button. Defaults to 'after' */
    @Input()
    @HostBinding('attr.labelPosition')
    get labelPosition(): 'top' | 'right' | 'bottom' | 'left' {
        return this._labelPosition
    }
    
    set labelPosition(value) {
        this._labelPosition = value;
    }
    
    private _labelPosition: 'top' | 'right' | 'bottom' | 'left' = 'right';
    
    /** Whether the radio button is disabled. */
    @Input()
    @HostBinding('attr.disabled')
    get disabled(): boolean {
        return this._disabled
    }
    
    set disabled(value: boolean) {
        const newDisabledState = coerceBooleanProperty(value);
        if (this._disabled !== newDisabledState) {
            this._disabled = newDisabledState;
            this._cd.markForCheck();
        }
    }
    
    /** Whether this radio button is checked. */
    @Input()
    @HostBinding('attr.checked')
    get checked(): boolean {
        return this._checked;
    }
    
    set checked(value: boolean) {
        const newCheckedState = coerceBooleanProperty(value);
        if (this._checked !== newCheckedState) {
            this._checked = newCheckedState;
            
            this._cd.markForCheck();
            
        }
    }
    
    @Input()
    get model() {
        return this._model
    }
    
    set model(newModel: any | any[]) {
        if (newModel !== this._model) {
            this._model = newModel
            // this.checked = _.isArray(newModel)
            //         ? !!<any[]>newModel.includes(this.value)
            //         : this.value === newModel
        }
    }
    
    private _model = null
    
    /** Theme color palette for the component. */
    @Input()
    @HostBinding('style.color')
    get color(): string | SafeStyle {
        return this.checked ? this._color : this._sanitizer.bypassSecurityTrustStyle('rgba(0, 0, 0, .54)')
    };
    
    set color(newColor: string | SafeStyle) {
        if (newColor && newColor !== this._color) {
            this._color = <string>newColor
        }
    }
    
    private _color = "#027be3"
    
    /** Whether this radio is checked. */
    private _checked = false;
    
    /** Whether this radio is disabled. */
    private _disabled: boolean;
    
    /** Whether this radio is required. */
    private _required: boolean;
    
    /** Value assigned to this radio. */
    private _value: any = null;
    
    /**
     *  Template for AzRadio content.
     * @docs-private
     */
    @ViewChildren(AzoraSlot)
    private _implicitContent: QueryList<AzoraSlot>;
    
    /**
     * Template provided inside the AzRadio view that will be used if present
     * @docs-private
     */
    @ContentChildren(AzoraSlot)
    private _explicitContent: QueryList<AzoraSlot>;
    
    @Output()
    change = new EventEmitter();
    
    @Output()
    modelChange: Subject<any> = new Subject()
    
    @Output()
    blur = new EventEmitter();
    @Output()
    focus = new EventEmitter();
    
    constructor(
            private _vcRef: ViewContainerRef,
            private _cd: ChangeDetectorRef,
            private _element: ElementRef,
            private _renderer: Renderer2,
            private _sanitizer: DomSanitizer
    ) {
    }
    
    ngOnInit() {
    
    }
    
    ngOnChanges(changes: SimpleChanges): void {
    }
    
    ngOnDestroy(): void {
    }
    
    
    onFocus = (e: Event) => {
        e.stopPropagation();
        
        this.focus.emit();
    };
    
    onBlur = (e: Event) => {
        e.stopPropagation();
        this.blur.emit();
    };
    
    _onInputClick(event: Event) {
        // We have to stop propagation for click events on the visual hidden input element.
        // By default, when a user clicks on a label element, a generated click event will be
        // dispatched on the associated input element. Since we are using a label element as our
        // root container, the click event on the `radio-button` will be executed twice.
        // The real click event will bubble up, and the generated click event also tries to bubble up.
        // This will lead to multiple click events.
        // Preventing bubbling for the second event will solve that issue.
        event.stopPropagation();
    }
    
    /**
     * Triggered when the radio button received a click or the input recognized any change.
     * Clicking on a label element, will trigger a change event on the associated input.
     */
    _onInputChange(event: Event) {
        // We always have to stop propagation on the change event.
        // Otherwise the change event, from the input element, will bubble up and
        // emit its event object to the `change` output.
        event.stopPropagation();
        
        this.checked = !this.checked;
        this._emitChangeEvent();
    }
    
    /** Dispatch change event with current value. */
    private _emitChangeEvent(): void {
        let emitValue
        if (this.checked) {
            emitValue = _.isArray(this.model) ? this.model.concat(this.value) : [this.model, this.value]
        } else {
            emitValue = _.without(this.model, this.value)
        }
        
        emitValue = emitValue.length > 1 ? emitValue : emitValue[0] || null
        
        this.modelChange.next(emitValue);
        this.change.next(emitValue)
    }
}

const COMPONENT = [AzoraCheckbox]

@NgModule({
    declarations: COMPONENT,
    exports: COMPONENT,
    entryComponents: COMPONENT
})
export class AzoraCheckboxModule {
}
